import React from 'react';
import { useIntl } from 'react-intl';
import useLanguageContext from '../../hooks/useLanguageContext';
import SupportedLanguages from '../../setup/intl/enums';
import { IconTick } from '../../assets/svg/icons';
import ScCard from '../../components/atoms/ScCard';
import ScButton from '../../components/atoms/ScButton';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import Badge from '../../components/atoms/Badge';
import { BadgeTone } from '../../components/atoms/Badge/enums';
import { OrderView, PaymentMethod } from './types';
import { iqd } from './format';
import { unitForms } from './liveData';
import {
  StyledConfirmBtns,
  StyledConfirmWrap,
  StyledReceiptCode,
  StyledReceiptHeader,
  StyledReceiptLine,
  StyledReceiptTotal,
  StyledRecapThumb,
} from './styles2';

interface ScreenConfirmedProps {
  orders: OrderView[];
  paymentMethod: PaymentMethod;
  onTrackOrders: () => void;
  onKeepSourcing: () => void;
}

// Pastel hue per line from its name (image source isn't on OrderView).
const hueFrom = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
};

const ScreenConfirmed: React.FC<ScreenConfirmedProps> = ({ orders, paymentMethod, onTrackOrders, onKeepSourcing }) => {
  const intl = useIntl();
  const { language } = useLanguageContext();
  const isAr = language === SupportedLanguages.Arabic;
  const cur = intl.formatMessage({ id: 'marketplace.currency', defaultMessage: 'IQD' });
  const multi = orders.length > 1;

  return (
    <StyledConfirmWrap>
      <div style={{ textAlign: 'center', marginBottom: 'var(--s-6)' }}>
        <StyledSuccessCircleC />
        <h1 className="h1" style={{ marginBottom: 8 }}>{intl.formatMessage({ id: 'marketplace.confirmed.title', defaultMessage: 'Order placed' })}</h1>
        <p className="muted" style={{ fontSize: 'var(--t-base)', margin: 0 }}>
          {multi
            ? intl.formatMessage({ id: 'marketplace.confirmed.subtitleMulti', defaultMessage: "We've created {n} purchase orders — one per supplier. Track them under Orders as they ship into your warehouse." }, { n: orders.length })
            : intl.formatMessage({ id: 'marketplace.confirmed.subtitle', defaultMessage: "We've sent your purchase order to the supplier. Track it under Orders as it ships into your warehouse." })}
        </p>
      </div>

      {orders.map((o) => (
        <ScCard key={o.id} pad={false} style={{ padding: 'var(--s-5)', marginBottom: 'var(--s-4)' }}>
          <StyledReceiptHeader>
            <div>
              <div className="eyebrow">{intl.formatMessage({ id: 'marketplace.purchaseOrder', defaultMessage: 'Purchase order' })}{multi && o.merchantName ? ` · ${o.merchantName}` : ''}</div>
              <StyledReceiptCode className="mono tnum">{o.code}</StyledReceiptCode>
            </div>
            <Badge tone={BadgeTone.Success} dot>{intl.formatMessage({ id: 'marketplace.confirmedBadge', defaultMessage: 'Confirmed' })}</Badge>
          </StyledReceiptHeader>
          <div style={{ marginTop: 'var(--s-3)' }}>
            {o.items.map((it) => {
              const itUnitForms = unitForms(it.stockUnit, language);
              return (
                <StyledReceiptLine key={it.listingId}>
                  <StyledRecapThumb $hue={hueFrom(it.name)}>📦</StyledRecapThumb>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--t-sm)' }}>{isAr ? it.nameAr || it.name : it.name}</div>
                    <div className="muted tnum" style={{ fontSize: 11 }}>{intl.formatMessage({ id: 'marketplace.countUnit', defaultMessage: '{n, plural, one {# {unit}} other {# {unitPlural}}}' }, { n: it.qty, ...itUnitForms })}</div>
                  </div>
                  <div className="mono tnum" style={{ fontWeight: 700, fontSize: 'var(--t-sm)' }}>{iqd(it.lineTotalIqd)}</div>
                </StyledReceiptLine>
              );
            })}
          </div>
          <StyledReceiptTotal>
            <span>
              {paymentMethod === 'wallet'
                ? intl.formatMessage({ id: 'marketplace.totalPaid', defaultMessage: 'Total paid' })
                : intl.formatMessage({ id: 'marketplace.totalDueTransfer', defaultMessage: 'Total due — via bank transfer, we will send the details' })}
            </span>
            <span className="mono tnum">{iqd(o.totalIqd)} <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 500 }}>{cur}</span></span>
          </StyledReceiptTotal>
        </ScCard>
      ))}

      <StyledConfirmBtns>
        <ScButton kind={ButtonKind.Outline} size={ButtonSize.Large} full onClick={onTrackOrders}>{intl.formatMessage({ id: 'marketplace.trackOrders', defaultMessage: 'Track in Orders' })}</ScButton>
        <ScButton kind={ButtonKind.Primary} size={ButtonSize.Large} full onClick={onKeepSourcing}>{intl.formatMessage({ id: 'marketplace.keepSourcing', defaultMessage: 'Keep sourcing' })}</ScButton>
      </StyledConfirmBtns>
    </StyledConfirmWrap>
  );
};

// Local success circle (avoids a cross-file style import cycle).
const StyledSuccessCircleC: React.FC = () => (
  <div
    style={{
      width: 72,
      height: 72,
      borderRadius: 'var(--r-full)',
      background: 'var(--success-soft)',
      color: 'var(--brand-green)',
      display: 'grid',
      placeItems: 'center',
      margin: '0 auto var(--s-4)',
    }}
  >
    <IconTick style={{ width: 36, height: 36 }} />
  </div>
);

export default ScreenConfirmed;
